/// <reference types="@types/google.maps" />

import { Injectable, NgZone } from '@angular/core';

export interface LatLng {
  lat: number;
  lng: number;
}

export interface CepAddressDetails {
  streetName?: string;
  neighborhood?: string;
  city?: string;
  state?: string;
}

const TOO_COARSE_RESULT_TYPES = ['country', 'administrative_area_level_1'];

@Injectable({ providedIn: 'root' })
export class GeocodingService {
  private geocoder: google.maps.Geocoder | null = null;
  private readonly TIMEOUT_MS = 5_000;

  constructor(private zone: NgZone) {}

  // -------------------------------------------------------------------------
  // Public API
  // -------------------------------------------------------------------------

  /**
   * Geocode a free-form address string and return its coordinates.
   *
   * This is a best-effort call — returns `null` on any failure (SDK not
   * loaded, timeout, no results, country- or state-only match, network
   * error). Never throws.
   */
  async geocodeAddress(address: string): Promise<LatLng | null> {
    if (!address?.trim()) return null;
    if (!this.ensureInitialized()) return null;

    const apiCall = new Promise<LatLng | null>((resolve) => {
      try {
        this.geocoder!.geocode({ address }, (results, status) => {
          this.zone.run(() => {
            if (
              status === google.maps.GeocoderStatus.OK &&
              results &&
              results.length > 0 &&
              !this.isTooCoarse(results[0])
            ) {
              const loc = results[0].geometry.location;
              resolve({ lat: loc.lat(), lng: loc.lng() });
            } else {
              resolve(null);
            }
          });
        });
      } catch {
        resolve(null);
      }
    });

    return this.race(apiCall, this.TIMEOUT_MS);
  }

  /**
   * Convenience wrapper for Brazilian CEP lookup.
   *
   * Builds a query from the CEP, the address fields found for it (when
   * known) and a country name so the Geocoding API can resolve it
   * internationally.
   */
  async geocodeByCep(
    cep: string,
    country = 'Brazil',
    details?: CepAddressDetails,
  ): Promise<LatLng | null> {
    const digits = cep.replace(/\D/g, '');
    if (!digits) return null;

    const formattedCep =
      digits.length === 8 ? `${digits.slice(0, 5)}-${digits.slice(5)}` : digits;
    const locality = [details?.city, details?.state].filter(Boolean).join(' - ');
    const parts = [
      details?.streetName,
      details?.neighborhood,
      locality,
      formattedCep,
      country,
    ].filter(Boolean);

    return this.geocodeAddress(parts.join(', '));
  }

  // -------------------------------------------------------------------------
  // Private helpers
  // -------------------------------------------------------------------------

  private isTooCoarse(result: google.maps.GeocoderResult): boolean {
    return (result.types ?? []).some((type) =>
      TOO_COARSE_RESULT_TYPES.includes(type),
    );
  }

  private ensureInitialized(): boolean {
    if (this.geocoder) return true;
    try {
      if (typeof google === 'undefined' || !google?.maps?.Geocoder) {
        return false;
      }
      this.geocoder = new google.maps.Geocoder();
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Race a call against a timeout.  On timeout (or any unhandled rejection)
   * silently returns `null` — geocoding is always best-effort.
   */
  private async race(
    call: Promise<LatLng | null>,
    ms: number,
  ): Promise<LatLng | null> {
    const timeout = new Promise<null>((resolve) =>
      setTimeout(() => resolve(null), ms),
    );
    try {
      return await Promise.race([call, timeout]);
    } catch {
      return null;
    }
  }
}
